import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../api';
import ProposalStudio, { HighlightRects, TypeBoxesStatic, TextBoxesStatic } from './ProposalStudio';
import PackSheet, { isUsPackSheet, auPageCount } from './PackSheet';
import { useRegion } from '../region';
import CoverPage, { blankCover } from './CoverPage';
import ProjectEditModal from './ProjectEditModal';
import { defaultSheet } from '../datasheet';
import { exportProjectPack } from '../exportPack';
import { fillOppNames, projectMatches, oppNumberOf } from '../projectOpp';

function coverPageOf(items, region) {
  if ((items || []).some(it => it.type === 'archive')) return null;
  if ((items || []).some(it => isUsPackSheet(it.data, region))) return null;
  const total = 1 + (items || []).reduce((n, it) => n + auPageCount(it.data), 0);
  return 'Page 1 of ' + total;
}

export default function ProposalStudioTab({ toast, isAdmin, initialProjectId }) {
  const region = useRegion();
  const [projects, setProjects] = useState(null);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState({ name: '', client: '', cover_sheet: blankCover(region) });
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [pq, setPq] = useState('');
  const [editData, setEditData] = useState(null);
  const exportRef = useRef(null);
  const saveTimer = useRef(null);
  const formRef = useRef(form);
  formRef.current = form;

  const openProject = useCallback((p) => {
    setSel(p);
    setForm({ name: p.name || '', client: p.client || '', cover_sheet: p.cover_sheet || blankCover(region) });
  }, [region]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.listProjects(isAdmin ? { all: true } : {});
        const items = r.items || [];
        setProjects(items);
        fillOppNames(items, byId => setProjects(ps => (ps || []).map(p => (byId[p.id] ? { ...p, ...byId[p.id] } : p))));
        if (initialProjectId) {
          const p = items.find(x => x.id === initialProjectId);
          if (p) openProject(p);
        }
      } catch (e) { console.error('Failed to load projects', e); setProjects([]); }
    })();
  }, [isAdmin, initialProjectId, openProject]);

  const scheduleSave = useCallback(() => {
    if (!sel) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      const f = formRef.current;
      const fresh = await api.updateProject(sel.id, { name: f.name, client: f.client, cover_sheet: f.cover_sheet });
      setProjects(ps => (ps || []).map(p => (p.id === fresh.id ? fresh : p)));
    }, 450);
  }, [sel]);
  const setField = (k, v) => { setForm(f => ({ ...f, [k]: v })); scheduleSave(); };
  const setCover = (k, v) => { setForm(f => ({ ...f, cover_sheet: { ...f.cover_sheet, [k]: v } })); scheduleSave(); };

  async function mutateItems(nextItems, msg) {
    const fresh = await api.updateProject(sel.id, { items: nextItems });
    setSel(fresh);
    setProjects(ps => (ps || []).map(p => (p.id === fresh.id ? fresh : p)));
    if (msg) toast(msg);
  }

  function openEdit(it) { setEditId(it.id); setEditData({ ...defaultSheet(), ...(it.data || {}) }); }
  async function saveEdit() {
    const next = ((sel && sel.items) || []).map(x => x.id === editId
      ? { ...x, data: editData, name: editData.name || x.name, code: editData.code || x.code, updatedAt: new Date().toISOString() }
      : x);
    setEditId(null); setEditData(null);
    await mutateItems(next, 'Project datasheet updated (source unchanged)');
  }

  async function exportPdf() {
    if (!sel || !exportRef.current) return;
    setBusy(true);
    try {
      await exportProjectPack(exportRef.current, (sel.items || []), form.name, (w) => toast(w, true));
      toast('PDF exported');
    } catch (e) { toast('Export failed: ' + e.message, true); }
    setBusy(false);
  }

  const items = (sel && sel.items) || [];
  const shown = (projects || []).filter(p => projectMatches(p, pq));

  if (!sel) {
    return (
      <div className="studio-pick" data-testid="studio-project-picker">
        <h2>Proposal Studio</h2>
        <p className="codegen-sub">Pick a project to build its client proposal — reorder pages, set the TYPE box refs, highlight core features with the highlighter, or let the AI match a quote automatically.</p>
        {projects === null ? (
          <div className="empty-note">Loading projects…</div>
        ) : projects.length === 0 ? (
          <div className="empty-note">No projects yet — create one in the Projects tab first.</div>
        ) : (
          <>
            <input className="input studio-pick-search" data-testid="studio-project-search" value={pq}
              onChange={e => setPq(e.target.value)} placeholder="Search name, opportunity # or client…" />
            <div className="studio-pick-grid">
              {shown.length === 0 ? <div className="empty-note" data-testid="studio-pick-empty">Nothing matched “{pq}”.</div> : shown.map(p => (
                <button key={p.id} className="studio-pick-card" data-testid={'studio-pick-' + p.id} onClick={() => openProject(p)}>
                  <span className="nm">{p.name}</span>
                  <span className="meta">
                    {oppNumberOf(p) && p.name !== oppNumberOf(p) && <span className="proj-opp-num" data-testid={'studio-pick-opp-' + p.id}>{oppNumberOf(p)}</span>}
                    {(p.items || []).length} datasheet(s){p.client ? ' · ' + p.client : ''}
                  </span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  const cover = form.cover_sheet || {};
  return (
    <>
      <ProposalStudio items={items} form={form} setField={setField} setCover={setCover}
        mutateItems={mutateItems} onEditItem={openEdit} onClose={() => setSel(null)}
        exportPdf={exportPdf} busy={busy} toast={toast} />
      <div className="export-host" ref={exportRef} aria-hidden="true">
        <CoverPage name={form.name} cover={cover} pageOf={coverPageOf(items, region)} />
        {items.filter(it => it.type !== 'archive').map(it => (
          <div data-export-page key={it.id}>
            <PackSheet data={it.data} region={region} pageId={'pp_' + it.id}
              overlay={<><HighlightRects rects={(it.highlights || {})['1']} /><TypeBoxesStatic boxes={(it.typeBoxes || {})['1']} /><TextBoxesStatic boxes={(it.textBoxes || {})['1']} /></>}
              overlay2={<><HighlightRects rects={(it.highlights || {})['2']} /><TypeBoxesStatic boxes={(it.typeBoxes || {})['2']} /><TextBoxesStatic boxes={(it.textBoxes || {})['2']} /></>} />
          </div>
        ))}
      </div>
      {editId && editData && (
        <ProjectEditModal editData={editData} setEditData={setEditData}
          onCancel={() => { setEditId(null); setEditData(null); }} onSave={saveEdit} />
      )}
    </>
  );
}
